import { yupResolver } from '@hookform/resolvers/yup'
import { useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { NavLink, useLocation } from 'react-router-dom'

import { Alert } from '../../components/Alert'
import { Button } from '../../components/Button'
import { Input } from '../../components/FormElements/Input'
import { PasswordInput } from '../../components/FormElements/PasswordInput'
import { useAuth } from '../../contexts/AuthContext'
import { getErrorMessage } from '../../utils/getErrorMessage'
import { createLoginSchema } from '../../validators/loginSchema'

interface FormValues {
  email: string
  password: string
}

const schema = createLoginSchema({
  email: {
    email: 'E-mail inválido',
    required: 'Campo obrigatório',
  },
  password: {
    required: 'Campo obrigatório',
  },
})

export const Login = () => {
  const location = useLocation()
  const [error, setError] = useState<string>()
  const { signIn } = useAuth()
  const { control, handleSubmit, formState } = useForm<FormValues>({
    mode: 'onChange',
    resolver: yupResolver(schema),
  })

  const onSignIn = async (data: FormValues) => {
    try {
      await signIn(data, (authUserType) => {
        const isOnPanel = location.pathname.includes('/painel')
        const isACrossSignIn = isOnPanel && authUserType === 'expert'

        if (isACrossSignIn) {
          throw new Error(
            `Sem permissão para acessar o painel com uma conta do tipo "${authUserType}"`
          )
        }

        return true
      })
    } catch (error) {
      setError(getErrorMessage(error))
    }
  }

  return (
    <div className="flex items-center justify-center min-h-screen mt-12">
      <div className="bg-white w-[500px] h-auto p-10 rounded-lg shadow-lg text-center">
        <h2 className="text-3xl font-normal text-gray-800 mb-8 text-left">
          Login
        </h2>

        {error && (
          <div className="mb-6">
            <Alert
              fullWidth
              variant="error"
              onClose={() => setError(undefined)}
            >
              Credenciais inválidas
            </Alert>
          </div>
        )}

        <form onSubmit={handleSubmit(onSignIn)} className="flex flex-col">
          <div className="mb-8 text-left tracking-wider">
            <Controller
              name="email"
              control={control}
              render={({
                field: { value = '', onChange },
                fieldState: { error, invalid, isDirty },
              }) => (
                <Input
                  label="E-mail*"
                  placeholder="example@email.com.br"
                  value={value}
                  onChange={onChange}
                  errorMessage={error?.message}
                  isValid={!invalid && isDirty}
                />
              )}
            />
          </div>

          <div className="mb-4 text-left tracking-wider">
            <Controller
              name="password"
              control={control}
              render={({
                field: { value = '', onChange },
                fieldState: { error, invalid },
              }) => (
                <PasswordInput
                  id="password"
                  label="Senha*"
                  placeholder="******"
                  value={value}
                  onChange={onChange}
                  errorMessage={error?.message}
                  isValid={!invalid}
                />
              )}
            />
          </div>

          <div className="mb-6 text-right">
            <NavLink
              to="/painel/forgot-password"
              className="text-sm font-medium text-gray-800 hover:underline hover:decoration-[#44A0C1] hover:underline-offset-2"
            >
              Esqueceu a senha?
            </NavLink>
          </div>

          <Button
            type="submit"
            isLoading={formState.isSubmitting}
            disabled={!formState.isValid}
            fullWidth
            variant="primary"
            size="small"
            borderRadius="no-rounded"
          >
            Entrar
          </Button>
        </form>
      </div>
    </div>
  )
}
